<script setup>
import { ref, onMounted, nextTick } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  command: {
    type: Function,
    required: true,
  },
});

const selectedIndex = ref(0);
const scrollContainer = ref(null);

// Helper function to check if item has a Lucide icon
const hasIcon = item => {
  return item.icon && item.icon.startsWith('i-lucide-');
};

const selectItem = index => {
  const item = props.items[index];
  if (item) {
    props.command(item);
  }
};

const scrollToSelected = async () => {
  await nextTick();

  if (!scrollContainer.value) return;

  const selectedElement = scrollContainer.value.querySelector(
    '.item-dropdown-item-selected'
  );
  if (!selectedElement) return;

  const container = scrollContainer.value;
  const containerRect = container.getBoundingClientRect();
  const selectedRect = selectedElement.getBoundingClientRect();

  // Calculate if the selected item is out of view
  const isAboveView = selectedRect.top < containerRect.top;
  const isBelowView = selectedRect.bottom > containerRect.bottom;

  if (isAboveView || isBelowView) {
    selectedElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }
};

const onKeyDown = ({ event }) => {
  if (event.key === 'ArrowUp') {
    selectedIndex.value =
      (selectedIndex.value + props.items.length - 1) % props.items.length;
    scrollToSelected();
    return true;
  }

  if (event.key === 'ArrowDown') {
    selectedIndex.value = (selectedIndex.value + 1) % props.items.length;
    scrollToSelected();
    return true;
  }

  if (event.key === 'Enter') {
    selectItem(selectedIndex.value);
    return true;
  }

  return false;
};

defineExpose({
  onKeyDown,
});

onMounted(() => {
  selectedIndex.value = 0;
});
</script>

<template>
  <div
    class="bg-white dark:bg-n-slate-2 border border-n-slate-3 dark:border-n-slate-8 rounded-lg shadow-lg overflow-hidden min-w-64 max-w-80"
  >
    <!-- Items -->
    <div ref="scrollContainer" class="max-h-60 overflow-y-auto p-2">
      <div
        v-for="(item, index) in items"
        :key="item.id"
        class="item-dropdown-item"
        :class="{
          'item-dropdown-item-selected': index === selectedIndex,
          'item-dropdown-item-tool': item.itemType === 'tool',
          'item-dropdown-item-attribute': item.itemType === 'attribute',
          'item-dropdown-item-library': item.itemType === 'library',
          'item-dropdown-item-visualization': item.itemType === 'visualization',
        }"
        @click="selectItem(index)"
        @mouseenter="selectedIndex = index"
      >
        <span
          class="item-type"
          :class="{
            'item-type-tool': item.itemType === 'tool',
            'item-type-attribute': item.itemType === 'attribute',
            'item-type-library': item.itemType === 'library',
            'item-type-visualization': item.itemType === 'visualization',
          }"
        >
          {{
            item.itemType === 'attribute'
              ? $t('ASSISTANT_MGMT.TOOLBOX.ATTRIBUTE_LABEL')
              : item.itemType === 'library'
                ? $t('ASSISTANT_MGMT.TOOLBOX.LIBRARY_LABEL')
                : item.itemType === 'visualization'
                  ? $t('ASSISTANT_MGMT.TOOLBOX.VISUALIZATION_LABEL')
                  : $t('ASSISTANT_MGMT.TOOLBOX.TOOL_LABEL')
          }}
        </span>
        <span class="item-action flex items-center gap-1">
          <Icon
            v-if="hasIcon(item)"
            :icon="item.icon"
            class="h-3 w-3 flex-shrink-0"
          />
          <span>{{ item.displayTitle || item.label || item.title }}</span>
        </span>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-if="!items.length"
      class="flex flex-col items-center justify-center py-8 px-4 text-center"
    >
      <div class="text-2xl mb-2 opacity-50">
        <svg class="w-8 h-8 mx-auto" fill="currentColor" viewBox="0 0 20 20">
          <path
            fill-rule="evenodd"
            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
            clip-rule="evenodd"
          />
        </svg>
      </div>
      <div class="text-sm text-n-slate-10 dark:text-n-slate-11">
        {{ $t('ASSISTANT_MGMT.TOOLBOX.NO_ITEMS_FOUND') }}
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Minimal dropdown styles inspired by toolbox pills */
.item-dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.5rem;
  margin-bottom: 0.125rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 0.875rem;
}

.item-dropdown-item:hover {
  background-color: #f8fafc;
}

.dark .item-dropdown-item:hover {
  background-color: rgba(30, 41, 59, 0.5);
}

.item-dropdown-item-selected {
  background-color: #f3f4f6;
  color: #604f91;
}

.item-dropdown-item-selected.item-dropdown-item-attribute {
  background-color: #fef3c7;
  color: #d97706;
}

.item-dropdown-item-selected.item-dropdown-item-library {
  background-color: #e1e9ff;
  color: #3a5bc7;
}

.item-dropdown-item-selected.item-dropdown-item-visualization {
  background-color: #ccfbf1;
  color: #0f766e;
}

.dark .item-dropdown-item-selected {
  background-color: #f3f4f6;
  color: #604f91;
}

.dark .item-dropdown-item-selected.item-dropdown-item-attribute {
  background-color: #fef3c7;
  color: #d97706;
}

.dark .item-dropdown-item-selected.item-dropdown-item-library {
  background-color: #e1e9ff;
  color: #3a5bc7;
}

.dark .item-dropdown-item-selected.item-dropdown-item-visualization {
  background-color: #ccfbf1;
  color: #0f766e;
}

/* Item type and action styles matching the toolbox */
.item-dropdown-item .item-type {
  background: white;
  border-radius: 0.5rem;
  padding: 0.125rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid #c2b9da;
  color: #604f91;
  flex-shrink: 0;
}

.item-dropdown-item .item-type-attribute {
  border: 1px solid #fbbf24;
  color: #d97706;
}

.item-dropdown-item .item-type-library {
  border: 1px solid #6366f1;
  color: #3a5bc7;
}

.item-dropdown-item .item-type-visualization {
  border: 1px solid #14b8a6;
  color: #0f766e;
}

.item-dropdown-item-selected .item-type {
  background: white;
  border-color: #a78bfa;
}

.item-dropdown-item-selected .item-type-attribute {
  background: white;
  border-color: #fbbf24;
  color: #d97706;
}

.item-dropdown-item-selected .item-type-library {
  background: white;
  border-color: #6366f1;
  color: #3a5bc7;
}

.item-dropdown-item-selected .item-type-visualization {
  background: white;
  border-color: #14b8a6;
  color: #0f766e;
}

.item-dropdown-item .item-action {
  font-weight: 500;
  color: #64748b;
  flex: 1;
  min-width: 0;
  font-size: 0.8125rem;
}

.item-dropdown-item-selected .item-action {
  color: #604f91;
}

.item-dropdown-item-selected.item-dropdown-item-attribute .item-action {
  color: #d97706;
}

.item-dropdown-item-selected.item-dropdown-item-library .item-action {
  color: #3a5bc7;
}

.item-dropdown-item-selected.item-dropdown-item-visualization .item-action {
  color: #0f766e;
}

.dark .item-dropdown-item .item-action {
  color: #94a3b8;
}

.dark .item-dropdown-item-selected .item-action {
  color: #604f91;
}

.dark .item-dropdown-item-selected.item-dropdown-item-attribute .item-action {
  color: #d97706;
}

.dark .item-dropdown-item-selected.item-dropdown-item-library .item-action {
  color: #3a5bc7;
}

.dark
  .item-dropdown-item-selected.item-dropdown-item-visualization
  .item-action {
  color: #0f766e;
}
</style>
